/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import moment, { Moment } from 'moment';
import {
  DataMask,
  DataMaskStateWithId,
  DataMaskWithId,
  Filter,
  FilterConfiguration,
  isFilterDivider,
} from '@superset-ui/core';
import { DEFAULT_FORM_DATA } from './types';

const ISO_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/** ownState: rolling「近 N 天」模式（非用户手选固定区间）。 */
export const DYNAMIC_DEFAULT_DAYS_OFFSET_KEY = '__dynamicDefaultDaysOffset';
export const DYNAMIC_DEFAULT_TIME_RANGE_KEY = '__dynamicDefaultTimeRangeKey';
/** ownState: user manually picked dates in the picker (not rolling default). */
export const USER_FIXED_RANGE_KEY = '__userFixedRange';

/** Calendar-day anchor for range math (startOf day). */
export function calendarDayOf(date: Moment): Moment {
  return date.clone().startOf('day');
}

/** True when the moment is midnight or end-of-day on a calendar date. */
export function isCalendarDayBoundary(date: Moment): boolean {
  if (date.isSame(date.clone().startOf('day'))) {
    return true;
  }
  return date.format('HH:mm:ss') === '23:59:59';
}

/** User picked an explicit time (not whole-day start/end). */
export function hasExplicitTime(date: Moment): boolean {
  return !isCalendarDayBoundary(date);
}

/** Superset SQL time filter uses [start, end): col >= start AND col < end */
export function toQueryStartMoment(date: Moment, showTime: boolean): Moment {
  if (showTime && hasExplicitTime(date)) {
    return date.clone();
  }
  return calendarDayOf(date);
}

export function toQueryEndMoment(date: Moment, showTime: boolean): Moment {
  if (showTime && hasExplicitTime(date)) {
    return date.clone().add(1, 'second');
  }
  return calendarDayOf(date).add(1, 'day').startOf('day');
}

export function toDisplayStartMoment(date: Moment, showTime: boolean): Moment {
  if (showTime && hasExplicitTime(date)) {
    return date.clone();
  }
  return calendarDayOf(date);
}

/** UI/persisted end: inclusive calendar day end (23:59:59) unless explicit time. */
export function toDisplayEndMoment(date: Moment, showTime: boolean): Moment {
  if (showTime && hasExplicitTime(date)) {
    return date.clone();
  }
  return calendarDayOf(date).endOf('day');
}

/** End-date picker value (shows 23:59:59 when format includes time). */
export function toPickerEndMoment(date: Moment, showTime: boolean): Moment {
  if (!showTime) {
    return calendarDayOf(date);
  }
  return toDisplayEndMoment(date, showTime);
}

/**
 * Parse persisted filterState end for UI refs.
 * Supports legacy endOf('day') (23:59:59) and exclusive-bound midnight.
 */
export function parseStoredEndMoment(
  storedEnd: string,
  storedStart: string,
  showTime: boolean,
): Moment {
  const end = moment(storedEnd);
  if (showTime) {
    if (hasExplicitTime(end)) {
      return end;
    }
    return calendarDayOf(end).endOf('day');
  }
  const endDay = calendarDayOf(end);
  if (
    end.isSame(endDay) &&
    end.hour() === 0 &&
    end.minute() === 0 &&
    end.second() === 0 &&
    endDay.isAfter(calendarDayOf(moment(storedStart)))
  ) {
    return endDay.subtract(1, 'day');
  }
  return endDay;
}

export function parseStoredStartMoment(
  storedStart: string,
  showTime: boolean,
): Moment {
  const start = moment(storedStart);
  if (showTime && hasExplicitTime(start)) {
    return start;
  }
  return calendarDayOf(start);
}

export function buildDataMaskPayload(
  start: Moment,
  end: Moment,
  showTime: boolean,
  dateFormat: string,
  options?: { ownState?: Record<string, string> },
): DataMask {
  const startStr = toDisplayStartMoment(start, showTime).format(dateFormat);
  const endStr = toDisplayEndMoment(end, showTime).format(dateFormat);
  const queryStartIso = toQueryStartMoment(start, showTime).format(ISO_FORMAT);
  const queryEndIso = toQueryEndMoment(end, showTime).format(ISO_FORMAT);
  const displayStartIso = toDisplayStartMoment(start, showTime).format(
    ISO_FORMAT,
  );
  const displayEndIso = toDisplayEndMoment(end, showTime).format(ISO_FORMAT);

  return {
    extraFormData: {
      time_range: `${queryStartIso} : ${queryEndIso}`,
    },
    filterState: {
      value: [displayStartIso, displayEndIso],
      label: `${startStr} ~ ${endStr}`,
    },
    ...(options?.ownState ? { ownState: options.ownState } : {}),
  };
}

export function buildRollingDefaultOwnState(
  daysOffset: number,
  start: Moment,
  end: Moment,
  showTime: boolean,
): Record<string, string> {
  const queryStartIso = toQueryStartMoment(start, showTime).format(ISO_FORMAT);
  const queryEndIso = toQueryEndMoment(end, showTime).format(ISO_FORMAT);
  return {
    [DYNAMIC_DEFAULT_DAYS_OFFSET_KEY]: String(daysOffset),
    [DYNAMIC_DEFAULT_TIME_RANGE_KEY]: `${queryStartIso} : ${queryEndIso}`,
  };
}

/** @deprecated use buildRollingDefaultOwnState */
export function buildDynamicDefaultOwnState(
  start: Moment,
  end: Moment,
  showTime: boolean,
): Record<string, string> {
  const queryStartIso = toQueryStartMoment(start, showTime).format(ISO_FORMAT);
  const queryEndIso = toQueryEndMoment(end, showTime).format(ISO_FORMAT);
  return {
    [DYNAMIC_DEFAULT_TIME_RANGE_KEY]: `${queryStartIso} : ${queryEndIso}`,
  };
}

export function parseDynamicDefaultDaysOffset(
  ownState?: Record<string, unknown>,
): number | undefined {
  const raw = ownState?.[DYNAMIC_DEFAULT_DAYS_OFFSET_KEY];
  if (raw === undefined || raw === null) {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function isRollingDefaultMode(
  ownState?: Record<string, unknown>,
): boolean {
  return parseDynamicDefaultDaysOffset(ownState) !== undefined;
}

/** Manual pick sets __userFixedRange; ChartProps defaults ownState to {} — not user-fixed. */
export function isUserFixedAbsoluteRange(
  filterStateValue: unknown,
  ownState?: Record<string, unknown>,
): boolean {
  if (filterStateValue == null) {
    return false;
  }
  if (isRollingDefaultMode(ownState)) {
    return false;
  }
  return ownState?.[USER_FIXED_RANGE_KEY] === '1';
}

export function getRollingDefaultTimeRangeKey(
  daysOffset: number,
  showTime: boolean,
): string {
  const range = calculateDefaultRange(daysOffset);
  return buildRollingDefaultOwnState(
    daysOffset,
    range.start,
    range.end,
    showTime,
  )[DYNAMIC_DEFAULT_TIME_RANGE_KEY];
}

/** True when today's absolute window differs from persisted ownState / first apply. */
export function shouldRollAbsoluteDefault(
  ownState: Record<string, unknown> | undefined,
  daysOffset: number,
  showTime: boolean,
): boolean {
  if (ownState?.[USER_FIXED_RANGE_KEY] === '1') {
    return false;
  }
  const hydratedOffset = parseDynamicDefaultDaysOffset(ownState);
  if (hydratedOffset == null) {
    return true;
  }
  if (hydratedOffset !== daysOffset) {
    return true;
  }
  const currentKey = ownState?.[DYNAMIC_DEFAULT_TIME_RANGE_KEY] as
    | string
    | undefined;
  return currentKey !== getRollingDefaultTimeRangeKey(daysOffset, showTime);
}

export function buildRollingDefaultDataMaskPayload(
  daysOffset: number,
  showTime: boolean,
  dateFormat: string,
): DataMask {
  const range = calculateDefaultRange(daysOffset);
  return buildDataMaskPayload(range.start, range.end, showTime, dateFormat, {
    ownState: buildRollingDefaultOwnState(
      daysOffset,
      range.start,
      range.end,
      showTime,
    ),
  });
}

export function msUntilNextLocalMidnight(): number {
  const nextMidnight = moment().startOf('day').add(1, 'day');
  return Math.max(0, nextMidnight.diff(moment()));
}

/** Last calendar day of a period, stored at startOf('day') for range math. */
export function lastCalendarDayOf(
  anchor: Moment,
  unit: moment.unitOfTime.StartOf,
): Moment {
  return anchor.clone().endOf(unit).startOf('day');
}

export function calculateDefaultRange(daysOffset: number): {
  start: Moment;
  end: Moment;
} {
  const today = moment().startOf('day');
  return {
    start: today.clone().subtract(daysOffset - 1, 'days'),
    end: today.clone(),
  };
}

export function formatIncludesTime(dateFormat: string): boolean {
  return (
    dateFormat.includes('HH') ||
    dateFormat.includes('mm') ||
    dateFormat.includes('ss')
  );
}

export function resolveDaysOffset(
  defaultDaysOffset?: number | string | null,
): number | undefined {
  if (defaultDaysOffset === undefined || defaultDaysOffset === null) {
    return undefined;
  }
  const parsed = Number(defaultDaysOffset);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

type DatePickerHydrateFilter = Pick<
  Filter,
  'id' | 'filterType' | 'controlValues' | 'defaultDataMask'
>;

function resolveDatePickerHydrateFormat(
  controlValues?: Record<string, unknown>,
): { dateFormat: string; showTime: boolean } {
  const dateFormat =
    (controlValues?.dateFormat as string | undefined) ||
    DEFAULT_FORM_DATA.dateFormat ||
    'YYYY-MM-DD';
  return { dateFormat, showTime: formatIncludesTime(dateFormat) };
}

function mergeHydrateDataMask(
  filter: DatePickerHydrateFilter,
  incomingMask?: DataMask,
): DataMaskWithId {
  return {
    id: filter.id,
    extraFormData: {},
    filterState: {},
    ownState: {},
    ...filter.defaultDataMask,
    ...incomingMask,
  } as DataMaskWithId;
}

/**
 * Pre-align rolling defaultDaysOffset before HYDRATE_DASHBOARD so the first
 * chart query uses the same mask DatePicker would emit on mount.
 */
export function resolveDatePickerDataMaskForHydrate(
  filter: DatePickerHydrateFilter,
  incomingMask?: DataMask,
): DataMaskWithId | null {
  if (filter.filterType !== 'filter_datepicker') {
    return null;
  }

  const daysOffset = resolveDaysOffset(
    filter.controlValues?.defaultDaysOffset as
      | number
      | string
      | null
      | undefined,
  );
  if (!daysOffset) {
    return null;
  }

  const baseMask = mergeHydrateDataMask(filter, incomingMask);
  const { dateFormat, showTime } = resolveDatePickerHydrateFormat(
    filter.controlValues,
  );

  if (
    isUserFixedAbsoluteRange(baseMask.filterState?.value, baseMask.ownState)
  ) {
    return null;
  }

  if (!shouldRollAbsoluteDefault(baseMask.ownState, daysOffset, showTime)) {
    return null;
  }

  return {
    ...baseMask,
    ...buildRollingDefaultDataMaskPayload(daysOffset, showTime, dateFormat),
    id: filter.id,
  };
}

function hasExplicitDatePickerDefaultValue(mask?: DataMask | null): boolean {
  const value = mask?.filterState?.value;
  return (
    value != null &&
    Array.isArray(value) &&
    value.length >= 2 &&
    value[0] != null &&
    value[1] != null
  );
}

/**
 * Filter config save — when only defaultDaysOffset is set (no 启用默认值),
 * persist rolling defaultDataMask instead of an empty mask.
 */
export function resolveDatePickerDefaultDataMaskForSave(
  filter: Pick<Filter, 'filterType' | 'controlValues' | 'defaultDataMask'>,
  options: {
    formDefaultDataMask?: DataMask | null;
    formIncludesDefaultDataMaskKey?: boolean;
  } = {},
): DataMask {
  const { formDefaultDataMask, formIncludesDefaultDataMaskKey = false } =
    options;

  if (filter.filterType !== 'filter_datepicker') {
    return (
      filter.defaultDataMask ?? {
        extraFormData: {},
        filterState: {},
        ownState: {},
      }
    );
  }

  if (
    formIncludesDefaultDataMaskKey &&
    hasExplicitDatePickerDefaultValue(formDefaultDataMask)
  ) {
    return formDefaultDataMask as DataMask;
  }

  const daysOffset = resolveDaysOffset(
    filter.controlValues?.defaultDaysOffset as
      | number
      | string
      | null
      | undefined,
  );
  if (daysOffset) {
    const { dateFormat, showTime } = resolveDatePickerHydrateFormat(
      filter.controlValues,
    );
    return buildRollingDefaultDataMaskPayload(daysOffset, showTime, dateFormat);
  }

  if (formIncludesDefaultDataMaskKey) {
    return (
      formDefaultDataMask ?? {
        extraFormData: {},
        filterState: {},
        ownState: {},
      }
    );
  }

  return (
    filter.defaultDataMask ?? {
      extraFormData: {},
      filterState: {},
      ownState: {},
    }
  );
}

/** Layer: Dashboard 初始化传输 — hydrate 前写入 rolling dataMask 真源 */
export function preprocessDataMaskForHydrate(
  filterConfig: FilterConfiguration,
  dataMask: DataMaskStateWithId = {},
): DataMaskStateWithId {
  const result = { ...dataMask };
  filterConfig.forEach(filter => {
    if (isFilterDivider(filter)) {
      return;
    }
    const resolved = resolveDatePickerDataMaskForHydrate(
      filter,
      result[filter.id],
    );
    if (resolved) {
      result[filter.id] = resolved;
    }
  });
  return result;
}
